import type {
  NavigationGuard,
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationRaw,
} from "vue-router";
import { isArray } from "es-toolkit/compat";

import { APP_CALENDARS_ROUTES, APP_CHAT_ROUTES, APP_ROUTES } from "@/Generated/Routes/paths";
import type { ChatRoom } from "@/Modules/Chat/Models/Domain/Chat";
import type { User } from "@/Modules/Models/Domain/User";
import type { Language } from "@/Common/Models/Domain/Language";

import useAuthStore from "@/Modules/Auth/Store";
import useTherapistStore from "@/Modules/Therapist/Store";
import useUserStore from "@/Modules/User/Store";
import useChatStore from "@/Modules/Chat/Store";
import useAppStore from "@/Modules/App/Store";
import useCustomerStore from "@/Modules/Customers/Store";
import usePushNotificationsStore from "@/Modules/PushNotifications/Store";
import useUserLegalAgreementStore from "@/Modules/LegalAgreement/Store";
import useSubscriptionStore from "@/Modules/Subscription/Store";
import useActivityCenterStore from "@/Modules/ActivityCenter/Store";
import useQuestionnaireStore from "@/Modules/Questionnaire/Store";
import useVideoCallStore from "@/Modules/VideoCall/Store";
import useProfessionalViewStore from "@/Modules/Professional/Store";
import useCalendarsStore from "@/Modules/Calendars/Store";

import { isValidString } from "@/Utils/String";
import { hasValue, isObject } from "@/Utils/Objects";
import { isTokenWillExpireSoon } from "@/Utils/JWT";
import { isApp } from "@/Utils/Ionic";
import { waitFor } from "@/Utils/Promises";
import { log } from "@/Utils/Debug";

import { config } from "@/Common/envConfig";
import { cleanCanonical } from "./utils";

export type GuardFunction = (
  to: RouteLocationNormalized,
  from?: RouteLocationNormalized
) => Promise<RouteLocationRaw | undefined>;

export const buildBeforeEnterChain =
  (guards: GuardFunction[] | GuardFunction): NavigationGuard =>
  async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    let i = 0;
    let result: RouteLocationRaw | undefined = undefined;
    const list = isArray(guards) ? guards : [guards];

    while (!hasValue(result) && i < list.length) {
      result = await list[i++]!(to, from);
    }

    if (hasValue(result)) {
      log(
        `GUARD_REDIRECT -> TO: ${to.path}, FROM: ${from.path}, RESULT_TO: ${(result as RouteLocationNormalized).name as string}`
      );
      next(result);
    } else {
      next();
    }
  };

export function authGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  const authStore = useAuthStore();
  const isUserLogged = authStore.isUserLogged;
  const isPrivateRoute = !isObject(to.meta) || !to.meta.isPublic;

  if (isPrivateRoute) {
    if (isUserLogged) {
      next();
    } else {
      next({ name: APP_ROUTES.login.root.name });
    }
  } else {
    if (to.path === APP_ROUTES.login.root.path && isUserLogged) {
      next({ name: APP_ROUTES.home.root.name });
    } else {
      next();
    }
  }
}

export function notificationsGuard(to: RouteLocationNormalized) {
  const isPrivateRoute = !to.meta?.isPublic;
  const userStore = useUserStore();
  const therapistStore = useTherapistStore();

  if (isPrivateRoute) {
    userStore.clearNotification();
    therapistStore.clearNotification();
  }
}

export const initWSGuard: GuardFunction = async () => {
  const chatStore = useChatStore();

  chatStore.initializeWS();

  return undefined;
};

export const initLoguedAppGuard = async () => {
  const authStore = useAuthStore();
  const appStore = useAppStore();
  const pushStore = usePushNotificationsStore();
  const userStore = useUserStore();
  const userLegalAgreementStore = useUserLegalAgreementStore();
  const subscriptionStore = useSubscriptionStore();
  const therapistStore = useTherapistStore();
  const activityCenterStore = useActivityCenterStore();
  const questionnaireStore = useQuestionnaireStore();
  const videoCallStore = useVideoCallStore();
  const chatStore = useChatStore();
  const professionalViewStore = useProfessionalViewStore();
  const calendarsStore = useCalendarsStore();

  const calls: Promise<unknown>[] = [];

  if (isValidString(authStore.userToken) && isTokenWillExpireSoon(authStore.userToken, 7)) {
    await authStore.refreshToken();
  }

  if (isApp() && !hasValue(userStore.user) && authStore.isUserLogged) {
    pushStore.init();
  }

  if (await userStore.setUser(true)) {
    chatStore.initializeWS();

    calls.push(questionnaireStore.fetchInitial());
    calls.push(activityCenterStore.getNumberOfNewNotifications());
    calls.push(videoCallStore.checkVideoCallInProgress());
    calls.push(calendarsStore.fetchCalendars());

    if (userStore.getUserInTherapistShow && isValidString(userStore.user!.therapistId)) {
      calls.push(professionalViewStore.fetchProfessionalInfo(userStore.user!.therapistId || ""));
    }

    if (userStore.getUserIsPatient) {
      calls.push(userLegalAgreementStore.fetchLegalAgreements((userStore.user! as User).id));
      calls.push(subscriptionStore.setSubscriptions()); /* TOOLBAR */
      calls.push(userStore.getB2bDocs());
    } else {
      calls.push(therapistStore.setTherapist());
    }
  } else {
    appStore.clearStores();
    await waitFor(0.3);

    return { name: APP_ROUTES.login.root.name };
  }

  await Promise.all(calls);

  if (userStore.getUserIsPatient && hasValue(userStore.user) && userStore.getUserInOnBoarding) {
    return { name: APP_ROUTES.onboarding.root.name };
  }

  return undefined;
};

export const chatCustomerGuard: GuardFunction = async (to: RouteLocationNormalized) => {
  const chatStore = useChatStore();
  const appStore = useAppStore();
  const userStore = useUserStore();
  const customerStore = useCustomerStore();

  appStore.openSideMenu();

  await userStore.setUser();

  if (chatStore.chatRooms.length === 0) {
    await chatStore.fetchChats();
  }

  let chatRoom: ChatRoom | undefined = undefined;

  if (userStore.getUserIsTherapist) {
    chatRoom = chatStore.chatRooms.find((c) => c.user.id === to.params.customerId);
  } else if (chatStore.chatRooms.length > 0) {
    chatRoom = chatStore.chatRooms[0];
  }

  if (chatRoom) {
    const calls: Promise<unknown>[] = [chatStore.setActiveChatRoom(chatRoom.id)];

    if (userStore.getUserIsTherapist) {
      calls.push(customerStore.fetchCustomer(chatRoom.user.id));
    }

    await Promise.all(calls);

    return undefined;
  } else {
    return { name: APP_CHAT_ROUTES.root.name };
  }
};

export const enableNavigationGuard = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const appStore = useAppStore();

  appStore.showNavigation();

  next();
};

export const isTherapistGuard: GuardFunction = async () => {
  const userStore = useUserStore();

  if (userStore.getUserIsTherapist) {
    return undefined;
  }

  return { name: APP_ROUTES.home.root.name };
};

export const isPatientGuard: GuardFunction = async () => {
  const userStore = useUserStore();

  if (userStore.getUserIsPatient) {
    return undefined;
  }

  return { name: APP_ROUTES.home.root.name };
};

export const onlyLanguageGuard =
  (langs: Language[]): GuardFunction =>
  async () => {
    const userStore = useUserStore();

    if (isValidString(userStore.user?.language) && langs.includes(userStore.user?.language)) {
      return undefined;
    }

    return { name: APP_ROUTES.home.root.name };
  };

export const loadCannonicalGuard: GuardFunction = async (to: RouteLocationNormalized) => {
  let canonical = config.appUrl;

  if (isValidString(to.meta?.canonical)) {
    canonical += cleanCanonical(to.meta.canonical);

    log(`Canonical: ${canonical}`);
  }

  to.meta.canonical = canonical;

  return undefined;
};

export const canSyncCalendars: GuardFunction = async () => {
  const therapistStore = useTherapistStore();

  if (therapistStore.therapist?.calendarIntegrations === true) {
    return undefined;
  }

  return { name: APP_CALENDARS_ROUTES.root.name };
};
